import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
  IconButton,
  Box,
  Paper,
  Alert,
  Card,
  CardContent,
  CardActions,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Fade,
  Slide,
  useTheme,
  alpha,
} from "@mui/material";
import { Edit, Delete, Download, TableChart, ViewModule, ViewList } from "@mui/icons-material";
import { collection, query, where, onSnapshot, deleteDoc, doc } from "firebase/firestore";
import { firestore as db } from "../components/firebase.config";
import { useAuth } from '../auth/AuthContext';
import ChecklistForm from "./ChecklistForm";

// Correct way to import jsPDF with autoTable
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// Import XLSX for Excel functionality
import * as XLSX from 'xlsx';


const UserChecklist = ({ email }) => {
  const [assignedChecklists, setAssignedChecklists] = useState([]);
  const [createdChecklists, setCreatedChecklists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'list'

  // Read from AuthContext — single source of truth.
  const { currentUser, tenantId } = useAuth();
  const userEmail = currentUser?.email || '';

  useEffect(() => {
    if (!userEmail || !tenantId) return;
    setLoading(true);
    setError(null);

    // Assigned checklists
    console.log("assignedChecklist details", tenantId, userEmail);
    const assignedQuery = query(
      collection(db, "assignedChecklists"),
      where("assignedTo", "==", userEmail),
      where("tenantId", "==", tenantId)
    );
    const unsubscribeAssigned = onSnapshot(assignedQuery, (snapshot) => {
      setAssignedChecklists(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), source: "assigned" })));
      setLoading(false);
    });

    // Created checklists
    const createdQuery = query(
      collection(db, "auditchecklists"),
      where("createdBy", "==", userEmail),
      where("tenantId", "==", tenantId)
    );
    const unsubscribeCreated = onSnapshot(createdQuery, (snapshot) => {
      setCreatedChecklists(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), source: "created" })));
    });

    return () => {
      unsubscribeAssigned();
      unsubscribeCreated();
    };
  }, [userEmail, tenantId]);

  const allChecklists = [
    ...assignedChecklists,
    ...createdChecklists.filter(
      c => !assignedChecklists.some(a => a.id === c.id)
    )
  ];

  const openForm = (checklist) => {
    setSelectedChecklist(checklist);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setSelectedChecklist(null);
  };

  const handleDeleteChecklist = async (id) => {
    if (window.confirm('Are you sure you want to delete this checklist?')) {
      try {
        await deleteDoc(doc(db, "assignedChecklists", id));
        console.log("Checklist deleted successfully");
      } catch (error) {
        console.error("Error deleting checklist:", error);
        setError("Error deleting checklist: " + error.message);
      }
    }
  };

  const generateExcel = (checklist) => {
    try {
      // Check if checklist data is valid
      if (!checklist) {
        throw new Error("No checklist data provided");
      }

      // Create a new workbook
      const workbook = XLSX.utils.book_new();

      // Prepare headers
      const headers = checklist.columns?.map((col) => col.name || col.id || 'Unnamed Column') || [];
      
      // Prepare data rows
      const rows = checklist.rows?.map((row, rowIndex) => {
        if (!row || !row.data) {
          console.warn(`Row ${rowIndex} has no data`);
          return checklist.columns?.map(() => '') || [];
        }
        
        return checklist.columns?.map((col) => {
          const cellData = row.data[col.id];
          
          // Handle different data types
          if (cellData === null || cellData === undefined) {
            return '';
          }
          
          if (typeof cellData === 'object') {
            // For attachment objects, show available URLs
            if (cellData && typeof cellData === 'object') {
              const urls = Object.entries(cellData)
                .filter(([key, value]) => key.includes('Url') && value)
                .map(([key, value]) => `${key.replace('Url', '')}: ${value}`)
                .join('; ');
              return urls || 'Attachment';
            }
            return JSON.stringify(cellData);
          }
          
          if (typeof cellData === 'boolean') {
            return cellData ? 'Yes' : 'No';
          }
          
          return String(cellData);
        }) || [];
      }) || [];
      
      let worksheetData = [];
      if (checklist.sections && checklist.sections.length > 0) {
        worksheetData.push(headers); // Add headers
        checklist.sections.forEach(section => {
          // Section name row
          const sectionRow = new Array(headers.length).fill('');
          sectionRow[0] = (section.type ? section.type + ' ' : '') + (section.title || '');
          worksheetData.push(sectionRow);

          // Section rows
          section.rows?.forEach(row => {
            const rowData = checklist.columns.map(col => {
              const cellData = row.data[col.id];
              // Handle different data types
              if (cellData === null || cellData === undefined) {
                return '';
              }
              
              if (typeof cellData === 'object') {
                // For attachment objects, show available URLs
                if (cellData && typeof cellData === 'object') {
                  const urls = Object.entries(cellData)
                    .filter(([key, value]) => key.includes('Url') && value)
                    .map(([key, value]) => `${key.replace('Url', '')}: ${value}`)
                    .join('; ');
                  return urls || 'Attachment';
                }
                return JSON.stringify(cellData);
              }
              
              if (typeof cellData === 'boolean') {
                return cellData ? 'Yes' : 'No';
              }
              
              return String(cellData);
            });
            worksheetData.push(rowData);
          });
        });
      } else {
        // Fallback to your current logic (no sections)
        if (headers.length > 0) {
          worksheetData.push(headers);
          worksheetData.push(...rows);
        } else {
          worksheetData.push(['No data available']);
        }
      }

      const mainWorksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      // Set column widths for better readability
      const columnWidths = headers.map(header => ({
        wch: Math.max(15, Math.min(50, header.length + 5))
      }));
      
      if (columnWidths.length > 0) {
        mainWorksheet['!cols'] = columnWidths;
      }

      // Add the main worksheet
      XLSX.utils.book_append_sheet(workbook, mainWorksheet, "Checklist Data");

      // Create a summary worksheet with checklist metadata
      const summaryData = [
        ['Checklist Information', ''],
        ['Name', checklist.name || 'Untitled Checklist'],
        ['Description', checklist.description || 'No description'],
        ['Assigned Date', checklist.assignedAt ? new Date(checklist.assignedAt).toLocaleDateString() : 'Not specified'],
        ['Assigned To', email],
        ['Total Columns', checklist.columns?.length || 0],
        ['Total Rows', checklist.rows?.length || 0],
        [''],
        ['Remarks', checklist.remarks || 'No remarks'],
      ];

      // Add main image URL if present
      if (checklist.imageUrl) {
        summaryData.push(['Main Image URL', checklist.imageUrl]);
      }

      const summaryWorksheet = XLSX.utils.aoa_to_sheet(summaryData);
      
      // Set column widths for summary sheet
      summaryWorksheet['!cols'] = [
        { wch: 20 }, // First column
        { wch: 50 }  // Second column
      ];

      // Add the summary worksheet
      XLSX.utils.book_append_sheet(workbook, summaryWorksheet, "Summary");

      // Generate filename
      const fileName = checklist.name 
        ? `${checklist.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.xlsx` 
        : "checklist.xlsx";

      // Save the Excel file
      XLSX.writeFile(workbook, fileName);
      
      console.log("Excel file generated successfully:", fileName);
      
    } catch (error) {
      console.error('Error generating Excel file:', error);
      setError(`Failed to generate Excel file: ${error.message}`);
      alert(`Failed to generate Excel file: ${error.message}`);
    }
  };

  


const generatePDF = (checklist) => {
  try {
    if (!checklist) throw new Error("No checklist data provided");

    const doc = new jsPDF({ orientation: 'landscape' });
    const pageWidth = doc.internal.pageSize.width;
    const margin = 10;
    let yOffset = 20;

    // Title
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text(checklist.name || "Untitled Checklist", margin, yOffset);
    yOffset += 15;

    // Description
    if (checklist.description) {
      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      const splitDescription = doc.splitTextToSize(`Description: ${checklist.description}`, pageWidth - 2 * margin);
      doc.text(splitDescription, margin, yOffset);
      yOffset += splitDescription.length * 6 + 5;
    }

    // Assigned Date
    if (checklist.assignedAt) {
      doc.setFontSize(10);
      doc.setFont(undefined, 'italic');
      const assignedDate = new Date(checklist.assignedAt).toLocaleDateString();
      doc.text(`Assigned: ${assignedDate}`, margin, yOffset);
      yOffset += 10;
    }

    // Setup Columns
    let columns = checklist.columns ? [...checklist.columns] : [];
    const attachmentsIdx = columns.findIndex(col => col.name?.toLowerCase().includes('attachment'));
    if (attachmentsIdx !== -1) {
      const [attachmentsCol] = columns.splice(attachmentsIdx, 1);
      columns.push(attachmentsCol); // move "Attachments" to the end
    }
    const headers = columns.map(col => col.name || col.id || 'Unnamed Column');

    // Build Table Rows
    let rows = [];

    if (checklist.sections?.length > 0) {
      checklist.sections.forEach(section => {
        const sectionTitle = `${section.type || ''}${section.title ? ` / ${section.title}` : ''}`;
        const sectionRow = new Array(headers.length).fill('');
        sectionRow[0] = sectionTitle;
        rows.push(sectionRow);

        section.rows?.forEach(row => {
          const rowData = columns.map(col => {
            const cellData = row.data[col.id];
            if (
              cellData === null || cellData === undefined ||
              (typeof cellData === 'string' && cellData.trim() === '') ||
              (typeof cellData === 'object' && Object.keys(cellData || {}).length === 0)
            ) return ' - ';

            if (col.name === "Attachments" && typeof cellData === 'object') {
              return Object.values(cellData)
                .filter(url => typeof url === 'string' && url.startsWith('http'))
                .join('\n') || ' - ';
            }

            if (typeof cellData === 'object') {
              return Object.values(cellData)
                .filter(val => typeof val === 'string')
                .map(val => val.length > 30 ? val.slice(0, 30) + '...' : val)
                .join(', ') || ' - ';
            }

            if (typeof cellData === 'boolean') return cellData ? 'Yes' : 'No';

            return String(cellData);
          });

          rows.push(rowData);
        });
      });
    }

    // Column Styles
    const columnStyles = {};
    headers.forEach((header, idx) => {
      const lower = header.toLowerCase();
      if (lower.includes('sr')) columnStyles[idx] = { cellWidth: 10 };
      else if (lower.includes('mms requirement')) columnStyles[idx] = { cellWidth: 26 };
      else if (lower.includes('guideline')) columnStyles[idx] = { cellWidth: 60 };
      else if (lower.includes('observation')) columnStyles[idx] = { cellWidth: 40 };
      else if (lower.includes('compliance')) columnStyles[idx] = { cellWidth: 40 };
      else if (lower.includes('max') || lower.includes('audit') || lower.includes('risk')) columnStyles[idx] = { cellWidth: 20 };
      else if (lower.includes('attachment')) columnStyles[idx] = { cellWidth: 50 };
      else columnStyles[idx] = { cellWidth: 30 };
    });

    // Render Table
    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: yOffset,
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 3,
        overflow: 'linebreak',
        halign: 'left',
        valign: 'top',
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9,
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      tableLineColor: [200, 200, 200],
      tableLineWidth: 0.1,
      margin: { left: margin, right: margin },
      tableWidth: 'wrap',
      columnStyles,
      didParseCell(data) {
        const isSectionRow = (
          data.row.raw &&
          data.row.raw[0] &&
          data.row.raw.slice(1).every(cell => cell === '') &&
          data.column.index === 0
        );

        if (isSectionRow) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.halign = 'center';
          data.cell.colSpan = headers.length;
        }

        // Remove text for Attachments column to avoid overlap with blue link
        const colName = headers[data.column.index]?.toLowerCase();
        if (
          data.section === 'body' &&
          colName?.includes('attachment') &&
          typeof data.cell.raw === 'string'
        ) {
          data.cell.text = ['']; // Clear the cell text so only the blue link is visible
        }
      },
      didDrawCell: function (data) {
        const colName = headers[data.column.index]?.toLowerCase();

        // Only apply to body cells, not header or footer
        if (
          data.section === 'body' &&
          colName?.includes('attachment') &&
          typeof data.cell.raw === 'string'
        ) {
          const urls = data.cell.raw.split('\n');
          let textY = data.cell.y + 3.5;

          urls.forEach((url) => {
            const lines = doc.splitTextToSize(url, data.cell.width - 4);
            const joinedText = lines.join('\n');

            // Only set blue color for link text
            doc.setTextColor(0, 0, 255);
            doc.textWithLink(joinedText, data.cell.x + 2, textY, { url });

            // Reset text color back to black
            doc.setTextColor(0, 0, 0);

            // Advance Y position
            textY += lines.length * 4.5;
          });
        }
      }


    });

    // ✅ Add Remarks
    yOffset = doc.lastAutoTable.finalY + 10;

    if (checklist.remarks) {
      if (yOffset > doc.internal.pageSize.height - 30) {
        doc.addPage();
        yOffset = 20;
      }

      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text("Remarks:", margin, yOffset);
      yOffset += 8;

      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      const remarksText = doc.splitTextToSize(checklist.remarks, pageWidth - 2 * margin);
      doc.text(remarksText, margin, yOffset);
      yOffset += remarksText.length * 5 + 10;
    }

    // ✅ Add Image URL
    if (checklist.imageUrl) {
      if (yOffset > doc.internal.pageSize.height - 20) {
        doc.addPage();
        yOffset = 20;
      }

      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text("Main Image URL:", margin, yOffset);
      yOffset += 6;

      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(0, 0, 255);
      doc.textWithLink(checklist.imageUrl, margin, yOffset, { url: checklist.imageUrl });
      doc.setTextColor(0, 0, 0);
    }

    // ✅ Save PDF
    const fileName = checklist.name
      ? `${checklist.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`
      : "checklist.pdf";

    doc.save(fileName);
    console.log("PDF generated successfully:", fileName);
  } catch (error) {
    console.error('Error generating PDF:', error);
    setError?.(`Failed to generate PDF: ${error.message}`);
    alert(`Failed to generate PDF: ${error.message}`);
  }
};





  

  const handleViewModeChange = (event, newMode) => {
    if (newMode) setViewMode(newMode);
  };

  const theme = useTheme();

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
      py: 4
    }}>
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box sx={{ 
          textAlign: 'center', 
          mb: 4,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          borderRadius: 3,
          p: 4,
          color: 'white',
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`
        }}>
          <Typography variant="h3" sx={{ 
            fontWeight: 700,
            mb: 2,
            textShadow: '0 2px 4px rgba(0,0,0,0.3)'
          }}>
            My Energy Audit Reports
          </Typography>
          <Typography variant="h6" sx={{ 
            opacity: 0.9,
            fontWeight: 400
          }}>
            Viewing checklists assigned to: <Chip 
              label={email} 
              sx={{ 
                backgroundColor: alpha(theme.palette.common.white, 0.2),
                color: 'white',
                fontWeight: 600,
                ml: 1
              }} 
            />
          </Typography>
        </Box>

        {/* View Mode Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Paper sx={{ 
            p: 1, 
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              aria-label="view mode"
              size="medium"
              sx={{
                '& .MuiToggleButton-root': {
                  border: 'none',
                  borderRadius: 2,
                  mx: 0.5,
                  transition: 'all 0.3s ease',
                  '&.Mui-selected': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    color: 'white',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`
                    }
                  },
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1)
                  }
                }
              }}
            >
              <ToggleButton value="card" aria-label="card view">
                <ViewModule sx={{ mr: 1 }} /> Card View
              </ToggleButton>
              <ToggleButton value="list" aria-label="list view">
                <ViewList sx={{ mr: 1 }} /> List View
              </ToggleButton>
            </ToggleButtonGroup>
          </Paper>
        </Box>

        {/* Error Alert */}
        {error && (
          <Fade in={!!error}>
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.2)}`
              }} 
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          </Fade>
        )}

        {/* Loading indicator */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress 
                size={60} 
                thickness={4}
                sx={{
                  color: theme.palette.primary.main,
                  mb: 2
                }}
              />
              <Typography variant="h6" color="text.secondary">
                Loading your checklists...
              </Typography>
            </Box>
          </Box>
        )}

      {/* Checklist display */}
        {!loading && allChecklists.length > 0 ? (
          viewMode === 'card' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 3, mt: 2 }}>
              {allChecklists.map((checklist, index) => (
                <Slide direction="up" in={true} timeout={300 + index * 100} key={checklist.id}>
                  <Card sx={{ 
                    minHeight: 220, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'space-between',
                    background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    borderRadius: 3,
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.25)}`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                    }
                  }}>
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{
                          width: 8,
                          height: 40,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                          borderRadius: 1,
                          mr: 2
                        }} />
                        <Typography variant="h6" sx={{ 
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                          lineHeight: 1.2
                        }}>
                          {checklist.name || "Untitled Checklist"}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
                        {checklist.description || "No description available"}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {checklist.assignedAt && (
                          <Chip 
                            size="small" 
                            label={`Assigned: ${new Date(checklist.assignedAt).toLocaleDateString()}`}
                            sx={{ 
                              backgroundColor: alpha(theme.palette.info.main, 0.1),
                              color: theme.palette.info.main,
                              fontSize: '0.75rem'
                            }}
                          />
                        )}
                        {checklist.columns && (
                          <Chip 
                            size="small" 
                            label={`${checklist.columns.length} Cols | ${checklist.rows?.length || 0} Rows`}
                            sx={{ 
                              backgroundColor: alpha(theme.palette.success.main, 0.1),
                              color: theme.palette.success.main,
                              fontSize: '0.75rem'
                            }}
                          />
                        )}
                      </Box>
                    </CardContent>
                    <CardActions sx={{ 
                      justifyContent: 'space-between', 
                      p: 2,
                      borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      background: alpha(theme.palette.background.default, 0.3)
                    }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit Energy Report" arrow>
                          <IconButton 
                            onClick={() => openForm(checklist)} 
                            size="small"
                            sx={{
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Checklist" arrow>
                          <IconButton 
                            onClick={() => handleDeleteChecklist(checklist.id)} 
                            size="small"
                            sx={{
                              backgroundColor: alpha(theme.palette.error.main, 0.1),
                              color: theme.palette.error.main,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.error.main, 0.2),
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Download Excel" arrow>
                          <IconButton 
                            onClick={() => generateExcel(checklist)} 
                            size="small"
                            sx={{
                              backgroundColor: alpha(theme.palette.success.main, 0.1),
                              color: theme.palette.success.main,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.success.main, 0.2),
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <TableChart fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download PDF" arrow>
                          <IconButton 
                            onClick={() => generatePDF(checklist)} 
                            size="small"
                            sx={{
                              backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                              color: theme.palette.secondary.main,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.secondary.main, 0.2),
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <Download fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardActions>
                  </Card>
                </Slide>
              ))}
            </Box>
          ) : (
            // List View
            <Paper sx={{ 
              mt: 2,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.1)}`
            }}>
              <List sx={{ p: 0 }}>
                {allChecklists.map((checklist, index) => (
                  <Fade in={true} timeout={200 + index * 50} key={checklist.id}>
                    <ListItem
                      secondaryAction={
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Edit Energy Report" arrow>
                            <IconButton 
                              onClick={() => openForm(checklist)} 
                              size="small"
                              sx={{
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                color: theme.palette.primary.main,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Checklist" arrow>
                            <IconButton 
                              onClick={() => handleDeleteChecklist(checklist.id)} 
                              size="small"
                              sx={{
                                backgroundColor: alpha(theme.palette.error.main, 0.1),
                                color: theme.palette.error.main,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.error.main, 0.2),
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download Excel" arrow>
                            <IconButton 
                              onClick={() => generateExcel(checklist)} 
                              size="small"
                              sx={{
                                backgroundColor: alpha(theme.palette.success.main, 0.1),
                                color: theme.palette.success.main,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.success.main, 0.2),
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <TableChart fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download PDF" arrow>
                            <IconButton 
                              onClick={() => generatePDF(checklist)} 
                              size="small"
                              sx={{
                                backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                                color: theme.palette.secondary.main,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.secondary.main, 0.2),
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <Download fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                      divider
                      sx={{ 
                        alignItems: 'flex-start',
                        py: 3,
                        px: 3,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.02)
                        },
                        transition: 'background-color 0.2s ease'
                      }}
                    >
                      <Box sx={{
                        width: 6,
                        height: 60,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        borderRadius: 1,
                        mr: 3,
                        flexShrink: 0
                      }} />
                      <ListItemText
                        primary={
                          <Typography variant="h6" sx={{ 
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            mb: 1
                          }}>
                            {checklist.name || "Untitled Checklist"}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.5 }}>
                              {checklist.description || "No description available"}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {checklist.assignedAt && (
                                <Chip 
                                  size="small" 
                                  label={`Assigned: ${new Date(checklist.assignedAt).toLocaleDateString()}`}
                                  sx={{ 
                                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                                    color: theme.palette.info.main,
                                    fontSize: '0.75rem'
                                  }}
                                />
                              )}
                              {checklist.columns && (
                                <Chip 
                                  size="small" 
                                  label={`${checklist.columns.length} Cols | ${checklist.rows?.length || 0} Rows`}
                                  sx={{ 
                                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                                    color: theme.palette.success.main,
                                    fontSize: '0.75rem'
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                  </Fade>
                ))}
              </List>
            </Paper>
          )
        ) : !loading ? (
            <Fade in={true}>
              <Paper sx={{ 
                p: 6, 
                textAlign: 'center', 
                mt: 4,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.1)}`
              }}>
                <Box sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3
                }}>
                  <ViewList sx={{ 
                    fontSize: 40, 
                    color: theme.palette.primary.main,
                    opacity: 0.7
                  }} />
                </Box>
                <Typography variant="h5" sx={{ 
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mb: 2
                }}>
                  No Energy Reports Found
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ 
                  maxWidth: 400,
                  mx: 'auto',
                  lineHeight: 1.6
                }}>
                  You don't have any assigned checklists yet. Contact your administrator to get checklists assigned to your email address.
                </Typography>
              </Paper>
            </Fade>
        ) : null
        }        {/* Checklist Form Dialog */}
        {isFormOpen && (
          <Dialog 
            open={isFormOpen} 
            onClose={closeForm} 
            maxWidth="xl" 
            fullWidth
            PaperProps={{
              sx: { 
                height: '90vh',
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
                backdropFilter: 'blur(20px)'
              }
            }}
          >
            <DialogTitle sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              color: 'white',
              p: 3
            }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                Edit Assigned Checklist
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Note: Structure editing is disabled for assigned checklists. You can only edit cell content.
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <ChecklistForm
                email={email}
                selectedChecklist={selectedChecklist}
                onClose={closeForm}
                isAssignedChecklist={true} // This tells the form it's editing an assigned checklist
                readOnlyStructure={true} // Prevents adding/removing columns, rows, and main image upload
                hideStructureControls={true} // Additional prop to explicitly hide controls
                tenantID={tenantId}
              />
            </DialogContent>
          </Dialog>
        )}
      </Container>
    </Box>
  );
};

export default UserChecklist;